import React from "react";
import { Trash2 } from "lucide-react";
import { HrmNotification } from "types/notifications";
import { getInitials } from "utils/getInitials";
import {
  getDateGroup,
  formatNotificationTimestamp,
  getHrmNotificationIcon,
  getHrmNotificationColors,
} from "utils/notificationsHelpers";

interface HrmNotificationCardProps {
  item: HrmNotification;
  isSelected?: boolean;
  onToggleSelect?: (uuid: string) => void;
  onToggleRead?: (uuid: string) => void;
  onRemove?: (uuid: string) => void;
  onClick: (n: HrmNotification) => void;
  isDropdownMode?: boolean;
}

const HrmNotificationCard: React.FC<HrmNotificationCardProps> = ({
  item,
  isSelected = false,
  onToggleSelect,
  onToggleRead,
  onRemove,
  onClick,
  isDropdownMode = false,
}) => {
  const group = getDateGroup(item.createdAt);
  const IconComponent = getHrmNotificationIcon(item.notificationType);
  const iconColors = getHrmNotificationColors(item.notificationType);

  if (isDropdownMode) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onClick(item)}
        onKeyDown={(e) => e.key === "Enter" && onClick(item)}
        className={`relative flex gap-3 px-4 py-3 transition-all duration-150 group border-b border-primary-subtle last:border-b-0 cursor-pointer ${
          item.isRead ? "hover:bg-white/3" : "bg-[#F59E0B]/5 hover:bg-[#F59E0B]/10"
        }`}
      >
        <div className="relative shrink-0">
          {item.actorProfileImageUrl ? (
            <img
              src={item.actorProfileImageUrl}
              alt={item.actorName || "User"}
              className="w-[34px] h-[34px] min-w-[34px] rounded-full object-cover shrink-0"
              referrerPolicy="no-referrer"
            />
          ) : item.actorName ? (
            <div className="w-[34px] h-[34px] min-w-[34px] rounded-full bg-[#9B6DFF]/20 text-[#9B6DFF] flex items-center justify-center text-[11px] font-bold shrink-0">
              {getInitials(item.actorName || "?")}
            </div>
          ) : (
            <div
              className={`w-[34px] h-[34px] min-w-[34px] rounded-full flex items-center justify-center ${iconColors.bg} ${iconColors.text} shrink-0`}
            >
              <IconComponent size={16} />
            </div>
          )}

          {(item.actorProfileImageUrl || item.actorName) && (
            <div
              className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center border-2 border-primary-variant ${iconColors.bg} ${iconColors.text}`}
            >
              <IconComponent size={10} />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 pr-8">
          {item.title && (
            <p className="text-gray-200 text-[13px] font-semibold leading-snug break-words">
              {item.title}
            </p>
          )}
          <p className="text-gray-300 text-xs leading-snug break-words">
            {item.notificationMessage}
          </p>
          <span className="text-[11px] text-gray-500 mt-1 block">
            {formatNotificationTimestamp(item.createdAt, group)}
          </span>
        </div>

        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-end h-6 min-w-6">
          {!item.isRead && (
            <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] group-hover:opacity-0 transition-opacity" />
          )}
          {onRemove && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(item.uuid);
              }}
              className="absolute right-0 opacity-0 group-hover:opacity-100 flex items-center justify-center p-1.5 rounded-lg border border-[#2A2F3D] text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/20 transition-all duration-150 shadow-sm"
              title="Delete notification"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // List Page Mode (Similar to NotificationCard.tsx)
  return (
    <div
      onClick={() => onClick(item)}
      className={`group relative flex items-start gap-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
        !item.isRead
          ? "bg-[#11131c] border-[#1e2330] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.5)]"
          : "bg-[#0c1017] border-[#161a24] hover:bg-[#11131c]"
      } ${isSelected ? "ring-1 ring-[#3b82f6] border-[#3b82f6]/50 bg-[#3b82f6]/5" : ""}`}
    >
      <div className="flex items-center justify-center h-10 pt-1 px-1">
        <label className="relative flex items-center justify-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect?.(item.uuid)}
            className="peer sr-only"
          />
          <div className="w-[18px] h-[18px] rounded-[4px] border-[1.5px] border-[#383f55] bg-transparent transition-all peer-checked:bg-[#3b82f6] peer-checked:border-[#3b82f6] peer-hover:border-[#64748b]" />
          <svg
            className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </label>
      </div>

      <div className="relative shrink-0 mt-1">
        {item.actorProfileImageUrl ? (
          <img
            src={item.actorProfileImageUrl}
            alt={item.actorName || "User"}
            className="w-10 h-10 rounded-full object-cover shrink-0 border border-[#2b3145]"
            referrerPolicy="no-referrer"
          />
        ) : item.actorName ? (
          <div className="w-10 h-10 rounded-full bg-[#9B6DFF]/20 text-[#9B6DFF] border border-[#2b3145] flex items-center justify-center text-sm font-bold shrink-0">
            {getInitials(item.actorName || "?")}
          </div>
        ) : (
          <div className={`w-10 h-10 rounded-full border border-[#2b3145] flex items-center justify-center ${iconColors.bg} ${iconColors.text} shrink-0`}>
             <IconComponent size={20} />
          </div>
        )}
        
        {(item.actorProfileImageUrl || item.actorName) && (
          <div
            className={`absolute -bottom-1 -right-1 w-[22px] h-[22px] rounded-full flex items-center justify-center border-[2.5px] border-[#11131c] ${iconColors.bg} ${iconColors.text}`}
          >
            <IconComponent size={11} strokeWidth={2.5} />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-1.5 pt-0.5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            {item.title && (
              <p className={`text-[14px] font-semibold leading-[19.5px] break-words ${!item.isRead ? "text-white" : "text-[#cbd5e1]"}`}>
                {item.title}
              </p>
            )}
            <p className={`text-[13px] leading-[19.5px] break-words ${!item.isRead ? "text-[#94a3b8]" : "text-[#64748b]"}`}>
              {item.notificationMessage}
            </p>
          </div>
          
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[11px] font-medium text-[#64748b]">
              {formatNotificationTimestamp(item.createdAt, group)}
            </span>
            <div className="flex items-center gap-1.5">
              {!item.isRead && (
                <div className="w-2 h-2 rounded-full bg-[#3b82f6] shadow-[0_0_8px_rgba(59,130,246,0.6)]" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HrmNotificationCard;
